// src/components/team/Groups.js
// FIXED: Proper display for both admin and endUser views

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
  setDoc,
} from "firebase/firestore";
import { Users, Search, Plus, Save, Edit2, CheckCircle, Eye, X } from "lucide-react";

// -----------------------------
// SYSTEM GROUP IDS (SOURCE OF TRUTH)
// -----------------------------
const SYS_GROUP_IDS = {
  ALL_STAFF: "grp_all_staff",
  INACTIVE_STAFF: "grp_inactive_staff",
  DELIVERY_STAFF: "grp_delivery_staff",
  WAREHOUSE_STAFF: "grp_warehouse_staff",
};

const AUTO_MANAGED_SYSTEM_GROUPS = new Set([SYS_GROUP_IDS.ALL_STAFF, SYS_GROUP_IDS.INACTIVE_STAFF]);
const RESTRICTED_ADMIN_GROUPS = new Set([SYS_GROUP_IDS.DELIVERY_STAFF, SYS_GROUP_IDS.WAREHOUSE_STAFF]);
const SYSTEM_GROUP_ID_SET = new Set([
  SYS_GROUP_IDS.ALL_STAFF,
  SYS_GROUP_IDS.INACTIVE_STAFF,
  SYS_GROUP_IDS.DELIVERY_STAFF,
  SYS_GROUP_IDS.WAREHOUSE_STAFF,
]);

// Helpers
const normalizeRole = (role) => (role || "").toString().trim().toLowerCase();
const normalizeStatus = (status) => (status || "").toString().trim().toLowerCase();

const isStaffRole = (role) => {
  const r = normalizeRole(role);
  return r === "owner" || r === "admin" || r === "enduser";
};

const isCustomerRole = (role) => normalizeRole(role) === "customer";

const getCompanyId = (me) => me?.companyId ;

const getOrgContext = (me) => {
  const companyId = getCompanyId(me);
  if (!companyId) return null;
  return { root: "companies", id: companyId };
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

const getUserGroupIds = (user) => safeArray(user?.groupIds || user?.teamIds || user?.groups || []).filter(Boolean);

const getUserCustomerGroupIds = (user) =>
  safeArray(user?.customerGroupIds || user?.customerGroups || []).filter(Boolean);

const getUserId = (u) => u?.id || u?.uid || null;
const getUserLabel = (u) => u?.fullName || u?.email || "Unnamed user";

const getInitials = (name, email) => {
  const n = (name || "").toString().trim();
  if (n) {
    const parts = n.split(" ").filter(Boolean);
    if (parts.length === 1) return (parts[0][0] || "U").toUpperCase();
    return ((parts[0][0] || "U") + (parts[parts.length - 1][0] || "U")).toUpperCase();
  }
  const e = (email || "").toString().trim();
  return e ? e[0].toUpperCase() : "U";
};

const getUserPhotoUrl = (user) =>
  (
    user?.photoURL ||
    user?.photoUrl ||
    user?.profilePhotoUrl ||
    user?.profilePhotoURL ||
    user?.profileImageUrl ||
    user?.avatarUrl ||
    user?.avatarURL ||
    user?.photo?.url ||
    ""
  ).toString();

const chunkArray = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const setEquals = (aArr, bArr) => {
  const a = new Set((aArr || []).filter(Boolean));
  const b = new Set((bArr || []).filter(Boolean));
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

// Color palette for group badges
const BADGE_COLORS = [
  "bg-blue-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
  "bg-cyan-500",
  "bg-teal-500",
  "bg-green-500",
  "bg-amber-500",
  "bg-orange-500",
  "bg-red-500",
];

const getGroupColor = (groupId) => {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = groupId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BADGE_COLORS[Math.abs(hash) % BADGE_COLORS.length];
};

const Groups = ({ 
  currentUserDoc, 
  firebaseUser, 
  teamUsers = [], 
  onRefreshTeamUsers, 
  onGroupsChanged,
  isAdminView = false, // ✅ NEW: Passed from parent
}) => {
  const app = getApp();
  const db = getFirestore(app);
  const auth = getAuth(app);

  const authUid = firebaseUser?.uid || auth.currentUser?.uid || null;

  const meFromTeamUsers = useMemo(() => {
    const uid = authUid;
    if (!uid) return null;
    return (teamUsers || []).find((u) => u?.id === uid || u?.uid === uid) || null;
  }, [teamUsers, authUid]);

  const me = useMemo(() => currentUserDoc || meFromTeamUsers || null, [currentUserDoc, meFromTeamUsers]);
  const org = useMemo(() => getOrgContext(me), [me]);

  // ✅ Use isAdminView prop instead of checking role
  const canManageGroups = isAdminView;

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState("");

  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupIsCustomer, setNewGroupIsCustomer] = useState(false);
  const [creating, setCreating] = useState(false);

  const [groupSearch, setGroupSearch] = useState("");
  const [activeGroupId, setActiveGroupId] = useState(null);

  const [userSearch, setUserSearch] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [savingMembers, setSavingMembers] = useState(false);

  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);

  // ✅ Get user's groupIds for endUser display
  const myGroupIds = useMemo(() => {
    return Array.isArray(me?.groupIds) ? me.groupIds : [];
  }, [me?.groupIds]);

  const activeUsers = useMemo(() => {
    return (teamUsers || [])
      .filter((u) => (u?.status || "Active").toString() !== "Deleted")
      .sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b)));
  }, [teamUsers]);

  const groupsById = useMemo(() => {
    const map = {};
    (groups || []).forEach((g) => {
      if (g?.id) map[g.id] = g;
    });
    return map;
  }, [groups]);

  const activeGroup = useMemo(() => {
    if (!activeGroupId) return null;
    return groupsById[activeGroupId] || null;
  }, [activeGroupId, groupsById]);

  const isSystemGroupSelected = !!(activeGroupId && SYSTEM_GROUP_ID_SET.has(activeGroupId));
  const isAutoManagedSystemGroupSelected = !!(activeGroupId && AUTO_MANAGED_SYSTEM_GROUPS.has(activeGroupId));
  const isRestrictedAdminGroupSelected = !!(activeGroupId && RESTRICTED_ADMIN_GROUPS.has(activeGroupId));

  // -----------------------------------------
  // Ensure system groups exist (admin only)
  // -----------------------------------------
  const ensureSystemGroups = useCallback(async () => {
    if (!org || !canManageGroups) return;
    const uid = authUid;
    if (!uid) return;

    const groupsColPath = [org.root, org.id, "groups"];

    const allStaffRef = doc(db, ...groupsColPath, SYS_GROUP_IDS.ALL_STAFF);
    const inactiveStaffRef = doc(db, ...groupsColPath, SYS_GROUP_IDS.INACTIVE_STAFF);
    const deliveryStaffRef = doc(db, ...groupsColPath, SYS_GROUP_IDS.DELIVERY_STAFF);
    const warehouseStaffRef = doc(db, ...groupsColPath, SYS_GROUP_IDS.WAREHOUSE_STAFF);

    const [allSnap, inactiveSnap, deliverySnap, warehouseSnap] = await Promise.all([
      getDoc(allStaffRef),
      getDoc(inactiveStaffRef),
      getDoc(deliveryStaffRef),
      getDoc(warehouseStaffRef),
    ]);

    const ops = [];

    ops.push(
      setDoc(
        doc(db, org.root, org.id),
        { staffGroupId: SYS_GROUP_IDS.ALL_STAFF, updatedAt: serverTimestamp() },
        { merge: true }
      )
    );

    if (!allSnap.exists()) {
      ops.push(
        setDoc(allStaffRef, {
          name: "All Staff",
          type: "internal",
          isSystem: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
        })
      );
    }

    if (!inactiveSnap.exists()) {
      ops.push(
        setDoc(inactiveStaffRef, {
          name: "Inactive Staff",
          type: "internal",
          isSystem: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
        })
      );
    }

    if (!deliverySnap.exists()) {
      ops.push(
        setDoc(deliveryStaffRef, {
          name: "Delivery Staff",
          type: "internal",
          isSystem: true,
          systemKey: "delivery_staff",
          description: "Admin-only operational group. Members can only move sample orders from Shipped to Delivered.",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
        })
      );
    }

    if (!warehouseSnap.exists()) {
      ops.push(
        setDoc(warehouseStaffRef, {
          name: "Warehouse Staff",
          type: "internal",
          isSystem: true,
          systemKey: "warehouse_staff",
          description: "Admin-only operational group. Members can only move sample orders from Approved to Preparation or Shipped.",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
        })
      );
    }

    if (ops.length) await Promise.all(ops);
  }, [db, org, canManageGroups, authUid]);

  const loadGroups = useCallback(async () => {
    // ✅ Both admin and endUser can load groups (rules allow list access)
    const uid = authUid;
    if (!uid) {
      console.warn("[Groups] Skipping loadGroups - no authenticated user yet.");
      return;
    }

    if (!org?.id) {
      setGroups([]);
      setGroupsError("Missing companyId on your user profile. Cannot load groups.");
      return;
    }

    setGroupsLoading(true);
    setGroupsError("");

    try {
      // Only admin ensures system groups exist
      if (canManageGroups) {
        await ensureSystemGroups();
      }

      const snap = await getDocs(collection(db, org.root, org.id, "groups"));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aSys = SYSTEM_GROUP_ID_SET.has(a.id) || a.isSystem === true;
          const bSys = SYSTEM_GROUP_ID_SET.has(b.id) || b.isSystem === true;
          if (aSys !== bSys) return aSys ? -1 : 1;
          return (a.name || "").localeCompare(b.name || "");
        });

      setGroups(list);
      setActiveGroupId((prev) => {
        if (prev && list.some((g) => g.id === prev)) return prev;
        return null;
      });

      if (typeof onGroupsChanged === "function") onGroupsChanged(list);
    } catch (err) {
      console.error("[Groups] loadGroups error:", err);
      const code = err?.code ? ` (${err.code})` : "";
      setGroups([]);
      setGroupsError(`Failed to load groups${code}.`);
    } finally {
      setGroupsLoading(false);
    }
  }, [db, org, canManageGroups, onGroupsChanged, authUid, ensureSystemGroups]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // -----------------------------------------
  // Counts
  // -----------------------------------------
  const groupMemberCounts = useMemo(() => {
    const counts = {};
    for (const u of activeUsers || []) {
      const role = normalizeRole(u?.role);
      const status = normalizeStatus(u?.status);

      if (isStaffRole(role)) {
        const gids = getUserGroupIds(u);
        for (const gid of gids) counts[gid] = (counts[gid] || 0) + 1;

        if (status === "inactive") {
          // no-op
        }
      } else if (isCustomerRole(role)) {
        const gids = getUserCustomerGroupIds(u);
        for (const gid of gids) counts[gid] = (counts[gid] || 0) + 1;
      }
    }
    return counts;
  }, [activeUsers]);

  const filteredGroups = useMemo(() => {
    const q = (groupSearch || "").toLowerCase().trim();
    if (!q) return groups;
    return (groups || []).filter((g) => (g?.name || "").toString().toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const handleCreateGroup = async () => {
    if (!org || !canManageGroups) return;
    const uid = authUid;

    if (!uid) {
      setGroupsError("Not signed in yet. Try again in a moment.");
      return;
    }

    if (!org.id) {
      setGroupsError("Missing companyId on your profile. Cannot create group.");
      return;
    }

    const name = (newGroupName || "").trim();
    if (!name) return;

    const normalized = name.toLowerCase();
    const exists = (groups || []).some((g) => (g?.name || "").toString().trim().toLowerCase() === normalized);
    if (exists) {
      setGroupsError("That group already exists.");
      return;
    }

    if (normalized === "all staff" || normalized === "inactive staff") {
      setGroupsError("That name is reserved for system groups.");
      return;
    }

    setCreating(true);
    setGroupsError("");

    try {
      const ref = await addDoc(collection(db, org.root, org.id, "groups"), {
        name,
        type: newGroupIsCustomer ? "customer" : "internal",
        isSystem: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: uid,
      });

      setNewGroupName("");
      setNewGroupIsCustomer(false);
      await loadGroups();
      setActiveGroupId(ref.id);
    } catch (err) {
      console.error("[Groups] create group error:", err);
      const code = err?.code ? ` (${err.code})` : "";
      setGroupsError(`Failed to create group${code}.`);
    } finally {
      setCreating(false);
    }
  };

  const handleRenameGroup = async () => {
    if (!canManageGroups || !org || !activeGroupId) return;
    const uid = authUid;

    if (!uid) {
      setGroupsError("Not signed in yet. Try again in a moment.");
      return;
    }

    if (!org.id) {
      setGroupsError("Missing companyId on your profile. Cannot rename group.");
      return;
    }

    if (isSystemGroupSelected || activeGroup?.isSystem === true) {
      setGroupsError("This is a system group and can't be renamed.");
      return;
    }

    const name = (renameValue || "").trim();
    if (!name) return;

    const normalized = name.toLowerCase();
    const exists = (groups || []).some(
      (g) => g.id !== activeGroupId && (g?.name || "").toString().trim().toLowerCase() === normalized
    );
    if (exists) {
      setGroupsError("Another group already has that name.");
      return;
    }

    setRenaming(true);
    setGroupsError("");

    try {
      await updateDoc(doc(db, org.root, org.id, "groups", activeGroupId), {
        name,
        updatedAt: serverTimestamp(),
      });
      await loadGroups();
    } catch (err) {
      console.error("[Groups] rename error:", err);
      const code = err?.code ? ` (${err.code})` : "";
      setGroupsError(`Failed to rename group${code}.`);
    } finally {
      setRenaming(false);
    }
  };

  // -----------------------------------------
  // Which users are eligible depends on group type
  // -----------------------------------------
  const eligibleUsers = useMemo(() => {
    const groupType = (activeGroup?.type || "internal").toString().toLowerCase();
    if (!activeGroupId) return [];

    if (groupType === "customer") {
      return (activeUsers || []).filter((u) => isCustomerRole(u?.role));
    }

    const staff = (activeUsers || []).filter((u) => isStaffRole(u?.role));
    if (activeGroupId === SYS_GROUP_IDS.INACTIVE_STAFF) {
      return staff.filter((u) => normalizeStatus(u?.status) === "inactive");
    }
    if (RESTRICTED_ADMIN_GROUPS.has(activeGroupId)) {
      return staff.filter((u) => {
        const role = normalizeRole(u?.role);
        const status = normalizeStatus(u?.status);
        return (role === "admin" || role === "owner") && status !== "inactive";
      });
    }
    return staff.filter((u) => normalizeStatus(u?.status) !== "inactive");
  }, [activeUsers, activeGroup?.type, activeGroupId]);

  const currentMemberIds = useMemo(() => {
    if (!activeGroupId) return [];
    const groupType = (activeGroup?.type || "internal").toString().toLowerCase();

    if (groupType === "customer") {
      return (eligibleUsers || [])
        .filter((u) => getUserCustomerGroupIds(u).includes(activeGroupId))
        .map((u) => getUserId(u))
        .filter(Boolean);
    }

    return (eligibleUsers || [])
      .filter((u) => getUserGroupIds(u).includes(activeGroupId))
      .map((u) => getUserId(u))
      .filter(Boolean);
  }, [activeGroupId, activeGroup?.type, eligibleUsers]);

  useEffect(() => {
    if (!activeGroupId) {
      setSelectedUserIds([]);
      setUserSearch("");
      setRenameValue("");
      setShowSelectedOnly(false);
      return;
    }

    setSelectedUserIds(currentMemberIds);
    setUserSearch("");
    setRenameValue(activeGroup?.name || "");
    setShowSelectedOnly(false);
  }, [activeGroupId, activeGroup?.name, currentMemberIds]);

  const hasMemberChanges = useMemo(() => !setEquals(currentMemberIds, selectedUserIds), [currentMemberIds, selectedUserIds]);

  const toggleUser = (userId) => {
    if (isAutoManagedSystemGroupSelected) return;
    setSelectedUserIds((prev) => {
      const set = new Set(prev || []);
      if (set.has(userId)) set.delete(userId);
      else set.add(userId);
      return Array.from(set);
    });
  };

  const filteredUsersBase = useMemo(() => {
    const q = (userSearch || "").toLowerCase().trim();
    if (!q) return eligibleUsers;

    return (eligibleUsers || []).filter((u) => {
      const name = (u?.fullName || "").toString().toLowerCase();
      const email = (u?.email || "").toString().toLowerCase();
      const role = normalizeRole(u?.role);
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [eligibleUsers, userSearch]);

  const filteredUsers = useMemo(() => {
    const base = filteredUsersBase || [];
    if (!showSelectedOnly) return base;
    const set = new Set((selectedUserIds || []).filter(Boolean));
    return base.filter((u) => set.has(getUserId(u)));
  }, [filteredUsersBase, showSelectedOnly, selectedUserIds]);

  const handleSaveMembers = async () => {
    if (!canManageGroups || !activeGroupId || !org) return;
    const uid = authUid;

    if (!uid) {
      setGroupsError("Not signed in yet. Try again in a moment.");
      return;
    }

    if (!org.id) {
      setGroupsError("Missing companyId on your profile. Cannot save members.");
      return;
    }

    if (isAutoManagedSystemGroupSelected) {
      setGroupsError("System groups are managed automatically and can't be edited here.");
      return;
    }

    setSavingMembers(true);
    setGroupsError("");

    try {
      const groupType = (activeGroup?.type || "internal").toString().toLowerCase();

      const currentMembers = new Set(
        (eligibleUsers || [])
          .filter((u) => {
            if (groupType === "customer") return getUserCustomerGroupIds(u).includes(activeGroupId);
            return getUserGroupIds(u).includes(activeGroupId);
          })
          .map((u) => getUserId(u))
          .filter(Boolean)
      );

      const desiredMembers = new Set((selectedUserIds || []).filter(Boolean));

      const updates = [];
      for (const id of desiredMembers) if (!currentMembers.has(id)) updates.push({ uid: id, action: "add" });
      for (const id of currentMembers) if (!desiredMembers.has(id)) updates.push({ uid: id, action: "remove" });

      if (updates.length === 0) return;

      const chunks = chunkArray(updates, 450);
      for (const chunk of chunks) {
        const batch = writeBatch(db);

        for (const u of chunk) {
          const user = (eligibleUsers || []).find((x) => getUserId(x) === u.uid);
          if (!user) continue;

          if (groupType === "customer") {
            const existing = getUserCustomerGroupIds(user);
            const set = new Set(existing);
            if (u.action === "add") set.add(activeGroupId);
            if (u.action === "remove") set.delete(activeGroupId);

            batch.update(doc(db, "users", u.uid), {
              customerGroupIds: Array.from(set),
              updatedAt: serverTimestamp(),
            });
          } else {
            const existing = getUserGroupIds(user);
            const set = new Set(existing);
            if (u.action === "add") set.add(activeGroupId);
            if (u.action === "remove") set.delete(activeGroupId);

            batch.update(doc(db, "users", u.uid), {
              groupIds: Array.from(set),
              updatedAt: serverTimestamp(),
            });
          }
        }

        await batch.commit();
      }

      if (typeof onRefreshTeamUsers === "function") {
        await onRefreshTeamUsers();
      }
    } catch (err) {
      console.error("[Groups] save members error:", err);
      const code = err?.code ? ` (${err.code})` : "";
      setGroupsError(`Failed to save members${code}.`);
    } finally {
      setSavingMembers(false);
    }
  };

  // ✅ ENDUSER VIEW: Read-only display of their groups
  if (!canManageGroups) {
    const myGroups = groups.filter((g) => myGroupIds.includes(g.id));

    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">View Only</h4>
              <p className="text-xs text-blue-700 mt-1">
                You can see the groups you belong to. Only admins or owners can create, edit, or delete groups.
              </p>
            </div>
          </div>
        </div>

        {groupsLoading ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
            <p className="text-sm text-slate-600">Loading groups...</p>
          </div>
        ) : myGroups.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-600 font-medium">
              You are not assigned to any groups yet.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Contact your admin to be added to groups.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {myGroups.map((group) => {
              const color = getGroupColor(group.id);
              const type = (group.type || "internal").toString().toLowerCase();
              const isSys = SYSTEM_GROUP_ID_SET.has(group.id) || group.isSystem === true;

              return (
                <div
                  key={group.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}>
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {group.name || "Untitled Group"}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded ${
                            type === "customer" ? "bg-purple-100 text-purple-700" : "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {type === "customer" ? "Customer" : "Internal"}
                        </span>
                        {isSys && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                            System
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ✅ ADMIN VIEW: Full management
  const groupTypeLabel =
    ((activeGroup?.type || "internal").toString().toLowerCase() === "customer") ? "Customer" : "Internal";

  const activeGroupContent = !activeGroup ? (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-600">Select a group</p>
        <p className="text-xs text-slate-400 mt-1">Choose a group to manage members</p>
      </div>
    </div>
  ) : (
    <div className="space-y-3">
      <div className="flex items-center justify-between pb-2 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded ${getGroupColor(activeGroupId)}`} />
          <h3 className="text-sm font-semibold text-slate-900">{activeGroup.name}</h3>
          <span
            className={`text-[9px] px-1.5 py-0.5 rounded ${
              groupTypeLabel === "Customer" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
            }`}
          >
            {groupTypeLabel}
          </span>
          {activeGroup.isSystem === true && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">SYSTEM</span>
          )}
          <span className="text-xs text-slate-500">• {groupMemberCounts[activeGroupId] || 0} members</span>
        </div>

        <button
          type="button"
          onClick={handleSaveMembers}
          disabled={savingMembers || !hasMemberChanges || isAutoManagedSystemGroupSelected}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" />
          {savingMembers ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
        <Edit2 className="w-3.5 h-3.5 text-slate-400" />
        <input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs"
          disabled={renaming || activeGroup.isSystem === true}
        />
        <button
          type="button"
          onClick={handleRenameGroup}
          disabled={
            renaming ||
            !renameValue.trim() ||
            renameValue.trim() === (activeGroup.name || "").trim() ||
            isAutoManagedSystemGroupSelected
          }
          className="px-3 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          {renaming ? "..." : "Rename"}
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setSelectedUserIds(filteredUsersBase.map((u) => getUserId(u)).filter(Boolean))}
          disabled={savingMembers || isAutoManagedSystemGroupSelected}
          className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          Select All
        </button>
        <button
          type="button"
          onClick={() => setSelectedUserIds([])}
          disabled={savingMembers || isAutoManagedSystemGroupSelected}
          className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          Clear
        </button>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showSelectedOnly}
            onChange={(e) => setShowSelectedOnly(e.target.checked)}
            className="w-3 h-3"
          />
          Selected only
        </label>
        <span className="text-xs text-slate-500">({selectedUserIds.length} selected)</span>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
        <input
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          placeholder={groupTypeLabel === "Customer" ? "Search customers..." : "Search staff..."}
          className="w-full pl-7 pr-2 py-2 border border-slate-300 rounded text-xs"
        />
      </div>

      <div className="border border-slate-200 rounded max-h-[50vh] overflow-y-auto">
        {filteredUsers.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">No users found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredUsers.map((u) => {
              const uid = getUserId(u);
              if (!uid) return null;
              const checked = selectedUserIds.includes(uid);
              const r = normalizeRole(u?.role);
              const locked = isAutoManagedSystemGroupSelected;
              const userPhotoUrl = getUserPhotoUrl(u);

              return (
                <label
                  key={uid}
                  className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 ${
                    locked ? "cursor-not-allowed opacity-80" : "cursor-pointer"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleUser(uid)}
                    className="w-3.5 h-3.5"
                    disabled={locked}
                  />
                  {userPhotoUrl ? (
                    <img
                      src={userPhotoUrl}
                      alt={getUserLabel(u)}
                      className="w-7 h-7 rounded-full object-cover ring-1 ring-slate-200"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
                      {getInitials(u.fullName, u.email)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900 truncate">{u.fullName || "Unnamed"}</div>
                    <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                    {r}
                  </span>
                  {checked && <CheckCircle className="w-3.5 h-3.5 text-green-600" />}
                </label>
              );
            })}
          </div>
        )}
      </div>

      {isAutoManagedSystemGroupSelected && (
        <div className="text-[11px] text-slate-500">
          System groups are enforced by rules. Membership changes happen through user status (Active/Inactive)
          and required system membership.
        </div>
      )}

      {isRestrictedAdminGroupSelected && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
          Only owners and admins can be added to this operational group. Delivery Staff can only move sample orders from
          <span className="font-semibold"> Shipped to Delivered</span>. Warehouse Staff can only move sample orders from
          <span className="font-semibold"> Approved to Preparation or Shipped</span>.
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="px-4 pt-3">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            {hasMemberChanges && (
              <span className="px-2 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-200">
                Unsaved
              </span>
            )}
            <button
              type="button"
              onClick={loadGroups}
              disabled={groupsLoading}
              className="px-3 py-1 text-xs text-slate-600 hover:text-slate-900"
            >
              {groupsLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {groupsError && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-xs">
          {groupsError}
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-12 divide-x divide-slate-200">
        {/* Left Sidebar */}
        <div className="col-span-12 md:col-span-4 p-3 bg-slate-50">
          <div className="flex gap-2 mb-2">
            <input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="New group name"
              className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs"
              disabled={creating}
            />
            <button
              type="button"
              onClick={handleCreateGroup}
              disabled={creating || !newGroupName.trim()}
              className="p-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              title="Create group"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Group type checkbox */}
          <label className="flex items-center gap-2 text-xs text-slate-700 mb-3">
            <input
              type="checkbox"
              checked={newGroupIsCustomer}
              onChange={(e) => setNewGroupIsCustomer(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Customer group (external)
          </label>

          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search groups..."
              className="w-full pl-7 pr-2 py-1 border border-slate-300 rounded text-xs"
            />
          </div>

          <div className="space-y-0.5 max-h-96 overflow-y-auto">
            {groupsLoading ? (
              <div className="p-3 text-xs text-slate-500 text-center">Loading...</div>
            ) : filteredGroups.length === 0 ? (
              <div className="p-3 text-xs text-slate-500 text-center">No groups</div>
            ) : (
              filteredGroups.map((g) => {
                const isActive = g.id === activeGroupId;
                const count = groupMemberCounts[g.id] || 0;
                const isSys = SYSTEM_GROUP_ID_SET.has(g.id) || g.isSystem === true;
                const color = getGroupColor(g.id);
                const type = (g.type || "internal").toString().toLowerCase();

                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setActiveGroupId(g.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                      isActive ? "bg-white shadow-sm" : "hover:bg-white/50"
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${color}`} />
                    <span className="flex-1 text-xs font-medium text-slate-900 truncate">{g.name || "Untitled"}</span>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded ${
                        type === "customer" ? "bg-purple-100 text-purple-700" : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {type === "customer" ? "CUS" : "INT"}
                    </span>
                    {isSys && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">SYS</span>
                    )}
                    <span className="text-[10px] text-slate-500 font-mono">[{count}]</span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="hidden md:block md:col-span-8 p-3">
          {activeGroupContent}
        </div>
      </div>

      {isMobile && activeGroup ? (
        <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden">
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setActiveGroupId("")}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
              >
                <X className="w-4 h-4" />
                Close
              </button>
              <div className="min-w-0 text-right">
                <div className="truncate text-sm font-semibold text-slate-900">{activeGroup?.name || "Group"}</div>
                <div className="text-[11px] text-slate-500">{groupMemberCounts[activeGroupId] || 0} members</div>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {activeGroupContent}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Groups;
